import {
  FC,
  ReactNode,
  ReactElement,
  Children,
  isValidElement,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react'
import SVGIcon from '@components/SVGIcon'
import FloatingModal from './FloatingModal'
import type { ModalVariant, ModalSize } from '../../types/modal'

type TreeModalContextType = {
  activeStepId: string
  navigateTo: (stepId: string) => void
  goBack: () => void
  resetToStart: () => void
  resetHistory: () => void
}

export const TreeModalContext = createContext<TreeModalContextType | undefined>(undefined)

export type ModalHeaderProps = {
  title: string
  showBackButton?: boolean
}

export const ModalHeader: FC<ModalHeaderProps> = props => {
  const { title, showBackButton = true } = props
  const context = useContext(TreeModalContext)
  const goBackFn = context?.goBack
  return (
    <>
      {showBackButton && goBackFn && (
        <button onClick={goBackFn} className="button-reset btn--icon qa-modal-back-button">
          <SVGIcon
            className="icon chevron-icon chevron-icon--left"
            xlinkHref="#icons-chevron-down"
            alt="Chevron pointing left"
          />
        </button>
      )}
      <h2 className="modal__title">{title}</h2>
    </>
  )
}

type ModalStepProps = {
  stepId: string
  header: ReactNode
  children?: ReactNode
}

export const ModalStep: FC<ModalStepProps> = ({ stepId, children }) => {
  const context = useContext(TreeModalContext)

  if (!context || context.activeStepId !== stepId) {
    return null
  }

  return <>{children}</>
}

type TreeModalProps = {
  show: boolean
  onClose: () => void
  initialStepId: string
  children?: ReactNode
  className?: string
  variant?: ModalVariant
  size?: ModalSize
}

const TreeModal: FC<TreeModalProps> = ({ show, initialStepId, children, onClose, className, variant, size }) => {
  const [activeStepId, setActiveStepId] = useState(initialStepId)
  const [_history, setHistory] = useState<string[]>([])

  // navigates to a specific step and saves the current step in history
  const navigateTo = (stepId: string) => {
    setHistory(prev => [...prev, activeStepId])
    setActiveStepId(stepId)
  }

  // goes back one step in the navigation history
  const goBack = () => {
    setHistory(prev => {
      const newHistory = [...prev]
      const lastStepId = newHistory.pop()
      if (lastStepId !== undefined) {
        setActiveStepId(lastStepId)
      }
      return newHistory
    })
  }

  // Navigates to initial step and resets history
  const resetToStart = () => {
    setActiveStepId(initialStepId)
    resetHistory()
  }

  // resets navigation history
  const resetHistory = () => {
    setHistory([])
  }

  const contextValue = {
    activeStepId,
    navigateTo,
    goBack,
    resetToStart,
    resetHistory,
  }

  useEffect(() => {
    if (show) {
      // Reset to start when modal show changes from false to true
      resetToStart()
    }
  }, [show])

  // Find the active step
  const activeChild = Children.toArray(children).find(
    (child): child is ReactElement<ModalStepProps> => isValidElement(child) && child.props.stepId === activeStepId
  )

  // Extract header from active step
  const header = activeChild?.props.header

  const computedClassName = className || 'tree-modal'

  return (
    <TreeModalContext.Provider value={contextValue}>
      <FloatingModal
        show={show}
        onClose={onClose}
        fullBleed={true}
        className={computedClassName}
        variant={variant} // Pass variant to FloatingModal
        size={size} // Pass size to FloatingModal
      >
        {header}
        {activeChild}
      </FloatingModal>
    </TreeModalContext.Provider>
  )
}

export default TreeModal
